import { useCallback, useSyncExternalStore } from "react";

/**
 * Le replay d'une prise, préparé derrière sa page.
 *
 * # Pourquoi un magasin hors de React
 *
 * La page d'une prise (`/takes/{id}`, `take-stage.tsx`) et le replay qui se
 * charge sous elle (`replay-context.tsx`) ne partagent aucun contexte : le
 * studio vit dans la coque, sous une frontière `Suspense`, et les pages des
 * routes à côté d'elle (`main-layout-shell.tsx`). Les réunir sous un même
 * fournisseur ferait sortir du prérendu toutes les pages de la coque. Ils se
 * parlent donc ici, et ne se disent que deux choses.
 *
 * # Ce qui passe
 *
 * Du replay vers la page : la prise est prête à regarder — ses notes chargées,
 * et une caméra qui a son image à la tête, ou aucune à attendre. C'est ce que
 * le bouton « Regarder » attend pour quitter son chargement.
 *
 * De la page vers le replay : on a cliqué « Regarder ». Le clic est la
 * demande de lecture, et c'est aussi le geste qui réveille le son ; le replay
 * lance donc la lecture en arrivant, même si l'audio n'a pas fini de se
 * réveiller à cet instant.
 */

type Behind = {
  /** L'identifiant de la prise. */
  readonly take: string;
  readonly ready: boolean;
  /** On a cliqué « Regarder » : la lecture est à lancer en arrivant. */
  readonly watch: boolean;
};

let current: Behind | null = null;

const listeners = new Set<() => void>();

function publish(next: Behind | null): void {
  current = next;

  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** Le replay dit où il en est : prêt à regarder, ou pas encore. */
export function announceTake(take: string, ready: boolean): void {
  if (current?.take === take && current.ready === ready) {
    return;
  }

  publish({ take, ready, watch: current?.take === take && current.watch });
}

/** Le replay s'en va : sa page n'a plus rien à attendre de lui. */
export function forgetTake(take: string): void {
  if (current?.take !== take) {
    return;
  }

  publish(null);
}

/** La page a été cliquée : la lecture est à lancer. */
export function askToWatch(take: string): void {
  if (current?.take !== take) {
    return;
  }

  publish({ ...current, watch: true });
}

/**
 * Le replay arrive : lui a-t-on demandé de jouer ? La demande ne vaut qu'une
 * fois, et se retire en se lisant.
 */
export function takeWatchAsked(take: string): boolean {
  if (current?.take !== take || !current.watch) {
    return false;
  }

  publish({ ...current, watch: false });

  return true;
}

/** Vrai quand la prise est prête à regarder derrière sa page. */
export function useTakeReady(take: string): boolean {
  const read = useCallback(() => current?.take === take && current.ready, [take]);

  return useSyncExternalStore(subscribe, read, () => false);
}
